// Nexus admin-ui — Ollama model categorisation + keep-alive helpers
// (P1-AI11 + P1-AI13).
//
// Lives in a .ts module (NOT .tsx) so the unit test can `node --test`
// it without pulling JSX through the loader. The runtime page tree
// re-exports these via ./shared so existing call sites don't change.
//
// Pure functions only — no I/O, no DOM, no React. Same shape as the
// K-Stack Gateway's preload categorisation heuristics (see
// K-Stack-Products/Gateway/local-gateway/src/services/ai-provider.ts).
/**
 * Parse a parameter-size string from Ollama's `/api/tags` `details`
 * block. Ollama emits values like "8.0B", "31.3B", "70.4B". Returns
 * billions of params as a number (so "31.3B" -> 31.3) or `null` if
 * the string can't be parsed.
 *
 * Exported for the unit test + the BrowseModelsModal column sort.
 */
export function parseParameterSizeB(raw) {
    if (typeof raw !== 'string')
        return null;
    const m = /^([0-9]+(?:\.[0-9]+)?)\s*[Bb]$/.exec(raw.trim());
    if (!m || !m[1])
        return null;
    const n = Number(m[1]);
    return Number.isFinite(n) ? n : null;
}
/**
 * Pull the family slug out of a model name or its `details.family`
 * field. Strategy:
 *
 *   1. Prefer `details.family` when present (Ollama's authoritative
 *      classification; one of 'llama', 'qwen2', 'gemma3', 'mistral',
 *      'nemotron', 'devstral', 'llava', ...). NOTE: we DON'T fold
 *      qwen3-coder/qwen3-vl into details.family because Ollama emits
 *      'qwen2' for some of them — the name-parsing path below is
 *      strictly more accurate for the variant suite.
 *
 *   2. Else parse the leading slug from the model name — everything
 *      before the first non-alphanumeric/period char. Then strip
 *      common suffixes (-coder, -vl, -instruct, -chat) so the
 *      variant suite collapses into ONE group:
 *
 *         qwen3-coder:30b → 'qwen3'
 *         qwen3-vl:30b    → 'qwen3'
 *         qwen3.5:14b     → 'qwen3' (the .5 minor is folded too)
 *         llava:7b        → 'llava'
 *         gemma4:31b      → 'gemma4'
 *
 *   3. Fallback when neither yields a sensible slug → 'other'.
 *
 * We RUN the name parsing first specifically so variant-suite siblings
 * share a family. Only when name-parsing yields 'other' do we fall
 * through to details.family.
 *
 * Exported for the unit test.
 */
export function familyOfModel(model) {
    const name = model.name.toLowerCase();
    // Take the chunk before the first ':' (Ollama tag separator).
    const beforeColon = name.split(':')[0] ?? name;
    // Take the chunk before the first '/' (org-prefix like 'library/mistral').
    const beforeSlash = beforeColon.includes('/')
        ? beforeColon.split('/').pop()
        : beforeColon;
    // Strip the common variant suffixes — order matters: longest first.
    const VARIANT_SUFFIXES = ['-instruct', '-coder', '-chat', '-vl'];
    let bare = beforeSlash;
    for (const suf of VARIANT_SUFFIXES) {
        if (bare.endsWith(suf)) {
            bare = bare.slice(0, -suf.length);
            break;
        }
    }
    // Fold ".N" minor versions back into the major version slug so
    // qwen3.5 and qwen3 land in the same group.
    bare = bare.replace(/\.\d+$/, '');
    if (/^[a-z][a-z0-9]*$/.test(bare))
        return bare;
    // Name parsing didn't yield a clean slug — fall through to
    // details.family. Belt-and-braces for exotic tag names like
    // 'huggingface/...:latest' that don't fit the usual shape.
    const detailsFamily = model.details?.family?.trim();
    if (detailsFamily && /^[a-z0-9]+$/i.test(detailsFamily)) {
        return detailsFamily.toLowerCase();
    }
    return 'other';
}
/**
 * Group an Ollama model list by family. Returned families are sorted
 * by `totalSize` descending — the heaviest family bubbles to the top
 * (so the operator sees their main daily-driver first), with
 * everything else following.
 *
 * Inside each family, models are sorted by parameter size descending
 * when `details.parameter_size` is parseable, else by name ascending.
 *
 * Pure function — no I/O, no caching. Re-runs cheaply on every render.
 */
export function categorizeOllamaModels(models) {
    const groups = new Map();
    for (const m of models) {
        const family = familyOfModel(m);
        let g = groups.get(family);
        if (!g) {
            g = { family, label: family, models: [], totalSize: 0 };
            groups.set(family, g);
        }
        g.models.push(m);
        g.totalSize += m.size ?? 0;
    }
    // Sort models within each family.
    for (const g of groups.values()) {
        g.models.sort((a, b) => {
            const pa = parseParameterSizeB(a.details?.parameter_size);
            const pb = parseParameterSizeB(b.details?.parameter_size);
            if (pa !== null && pb !== null && pa !== pb)
                return pb - pa;
            if (pa !== null && pb === null)
                return -1;
            if (pa === null && pb !== null)
                return 1;
            return a.name.localeCompare(b.name);
        });
    }
    // Sort families by total size desc; ties broken by alpha.
    return [...groups.values()].sort((a, b) => {
        if (a.totalSize !== b.totalSize)
            return b.totalSize - a.totalSize;
        return a.family.localeCompare(b.family);
    });
}
/**
 * Read the per-model keep_alive preference out of a host's metadata
 * bag. Returns `undefined` when no row exists for the given model —
 * caller (ModelsPanel toggle) renders that as "Keep hot: off".
 */
export function readKeepAliveForModel(metadata, modelName) {
    if (!metadata || typeof metadata !== 'object')
        return undefined;
    const map = metadata.ollamaKeepAliveByModel;
    if (!map || typeof map !== 'object' || Array.isArray(map))
        return undefined;
    const v = map[modelName];
    return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}
//# sourceMappingURL=categorize.js.map